/**
 * Shipment `exception_flags` bitmask ↔ severity strings (device-api / event rows).
 * Aligned with gpx-engine `ShipmentExceptionFlags`.
 */

export const SHIPMENT_EXCEPTION_BIT_INFO = 1;

export const SHIPMENT_EXCEPTION_BIT_WARNING = 2;

export const SHIPMENT_EXCEPTION_BIT_CRITICAL = 4;

/** Text severities used on exception events and derived from flags. */
export type ShipmentExceptionSeverity = 'info' | 'warning' | 'critical';

export const SHIPMENT_EXCEPTION_SEVERITY_ORDER: ShipmentExceptionSeverity[] = [
  'critical',
  'warning',
  'info',
];

const SEVERITY_TO_BIT: Record<ShipmentExceptionSeverity, number> = {
  info: SHIPMENT_EXCEPTION_BIT_INFO,
  warning: SHIPMENT_EXCEPTION_BIT_WARNING,
  critical: SHIPMENT_EXCEPTION_BIT_CRITICAL,
};

/**
 * Decode shipment `exception_flags` into severity strings (e.g. `critical`, `warning`).
 */
export function exceptionFlagsToSeverities(
  flags: number | null | undefined,
): ShipmentExceptionSeverity[] {
  const value = flags ?? 0;
  if (value <= 0) {
    return [];
  }

  return SHIPMENT_EXCEPTION_SEVERITY_ORDER.filter(
    (severity) => (value & SEVERITY_TO_BIT[severity]) !== 0,
  );
}

/**
 * Encode severity strings into a shipment `exception_flags` bitmask.
 */
export function severitiesToExceptionFlags(
  severities: Iterable<string>,
): number {
  let flags = 0;
  for (const raw of severities) {
    const severity = normalizeExceptionSeverity(raw);
    if (severity) {
      flags |= SEVERITY_TO_BIT[severity];
    }
  }
  return flags;
}

export function normalizeExceptionSeverity(
  severity: string,
): ShipmentExceptionSeverity | null {
  const s = severity.trim().toLowerCase();
  if (s === 'critical' || s === 'high') {
    return 'critical';
  }
  if (s === 'warning' || s === 'medium') {
    return 'warning';
  }
  if (s === 'info' || s === 'low') {
    return 'info';
  }
  return null;
}
